import type { ColorTheme } from "./resolve_site_color_theme";

// What the user picked; SYSTEM follows the device's light/dark setting.
export type ThemePreference = ColorTheme | "SYSTEM";

export const THEME_PREFERENCES: ThemePreference[] = ["LIGHT", "DARK", "SYSTEM"];

export const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

export const THEME_STORAGE_KEY = "appThemePreference";
// Written as "LIGHT" on every load before the device mode existed, so a stored LIGHT there
// is the old default, not a choice. index.html reads both keys by the same rule.
export const LEGACY_THEME_STORAGE_KEY = "appTheme";

const isThemePreference = (raw: unknown): raw is ThemePreference =>
	THEME_PREFERENCES.includes(raw as ThemePreference);

// The new key wins; from the legacy one only DARK/SYSTEM survive — those took a tap.
// Otherwise the device's theme is the default.
export const pickThemePreference = (stored: unknown, legacy: unknown): ThemePreference => {
	if (isThemePreference(stored)) return stored;
	if (legacy === "DARK" || legacy === "SYSTEM") return legacy;
	return "SYSTEM";
};

// The order a single theme button steps through.
export const nextThemePreference = (current: ThemePreference): ThemePreference =>
	THEME_PREFERENCES[(THEME_PREFERENCES.indexOf(current) + 1) % THEME_PREFERENCES.length];

export const resolveThemePreference = (
	preference: ThemePreference,
	isSystemDark: boolean,
): ColorTheme => {
	if (preference === "SYSTEM") return isSystemDark ? "DARK" : "LIGHT";
	return preference;
};

const readJson = (key: string): unknown => {
	try {
		return JSON.parse(localStorage.getItem(key) ?? "null");
	} catch {
		return null;
	}
};

export const readThemePreference = (): ThemePreference =>
	pickThemePreference(readJson(THEME_STORAGE_KEY), readJson(LEGACY_THEME_STORAGE_KEY));

export const writeThemePreference = (preference: ThemePreference) => {
	try {
		localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(preference));
		localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
	} catch {
		// Storage unavailable (private mode): the choice lives for this visit only.
	}
};

export const isSystemDarkNow = (): boolean => !!window.matchMedia?.(SYSTEM_DARK_QUERY).matches;
